// Variant Picker - Gestion de la sélection de variantes produit

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.sectionId = this.dataset.section;
  }

  connectedCallback() {
    this.addEventListener("click", this.onVariantClick.bind(this));
    this.updateSelectedVariant();
    this.setupClickOutside();
  }

  setupClickOutside() {
    // Fermer les dropdowns quand on clique à l'extérieur
    document.addEventListener("click", (event) => {
      const details = this.querySelectorAll("details[open]");
      details.forEach((detail) => {
        if (!detail.contains(event.target)) {
          detail.open = false;
        }
      });
    });
  }

  onVariantClick(event) {
    const button = event.target.closest(".variant-option-button");
    if (!button || button.disabled) return;

    event.preventDefault();

    const optionPosition = button.dataset.optionPosition;
    const value = button.dataset.value;

    // Mettre à jour le radio input caché correspondant
    const hiddenInput = this.querySelector(`input[data-option-position="${optionPosition}"][value="${value}"]`);
    if (hiddenInput) {
      hiddenInput.checked = true;
    }

    // Mettre à jour tous les boutons de cette option
    this.querySelectorAll(`button[data-option-position="${optionPosition}"]`).forEach((btn) => {
      btn.classList.remove("bg-wolf-white", "text-pinkyJeannette", "font-medium");
    });
    button.classList.add("bg-wolf-white", "text-pinkyJeannette", "font-medium");

    this.updateSelectedLabel(button);
    this.updateSelectedVariant();
  }

  updateSelectedLabel(button) {
    const optionPosition = button.dataset.optionPosition;
    const valueName = button.dataset.valueName;
    const optionName = button.dataset.optionName;
    const colorRgb = button.dataset.colorRgb;
    const swatchImage = button.dataset.swatchImage;

    // Trouver le summary à mettre à jour
    const selectorGroup = button.closest(".variant-selector-group");
    if (!selectorGroup) return;

    const selectedLabel = selectorGroup.querySelector(`[data-selected-label="${optionPosition}"]`);
    const details = selectorGroup.querySelector("details");
    if (!selectedLabel) return;

    // Construire le HTML avec swatch si disponible
    let html = '<span class="flex items-center gap-2">';

    if (colorRgb) {
      html += `<span class="inline-block w-4 h-4 rounded-full flex-shrink-0 border border-wolf-light" style="background-color: rgb(${colorRgb})"></span>`;
    } else if (swatchImage) {
      html += `<img src="${swatchImage}" alt="${valueName}" class="inline-block w-4 h-4 rounded-full flex-shrink-0 border border-wolf-light object-cover">`;
    }

    html += `<span>${optionName} : ${valueName}</span>`;
    html += "</span>";

    selectedLabel.innerHTML = html;

    // Fermer le details après sélection
    if (details) {
      details.open = false;
    }
  }

  getSelectedOptions() {
    const inputs = this.querySelectorAll(".variant-input:checked");
    return Array.from(inputs).map((input) => input.value);
  }

  updateSelectedVariant() {
    const selectedOptions = this.getSelectedOptions();
    const product = window.productData && window.productData[this.productId];

    if (!product || !product.variants) {
      return;
    }

    // Trouver la variante correspondante
    const variant = product.variants.find((v) => {
      return v.options.every((opt, idx) => opt === selectedOptions[idx]);
    });

    if (variant) {
      this.updateVariantId(variant.id);
      this.updatePrice(variant.price);
      this.updateStock(variant.inventory_quantity, variant.id);
      this.updateURL(variant.id);

      // Événement personnalisé pour autres composants
      this.dispatchEvent(
        new CustomEvent("variant-change", {
          detail: { variant },
          bubbles: true,
        })
      );
    }
  }

  updateVariantId(variantId) {
    const form = document.getElementById(`product-form-${this.sectionId}`);
    if (!form) return;

    let input = form.querySelector('input[name="id"]');
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = "id";
      form.appendChild(input);
    }
    input.value = variantId;
  }

  updatePrice(price) {
    const priceElement = document.querySelector("[data-variant-price]");
    if (priceElement && price) {
      // Shopify price est en centimes
      const formattedPrice = (price / 100).toFixed(2).replace(".", ",") + " €";
      priceElement.textContent = formattedPrice;
    }
  }

  updateStock(quantity, variantId = null) {
    const stockElement = document.querySelector("[data-stock-status]");
    const addToCartButton = document.querySelector("[data-add-to-cart]");
    const stockNotifyContainer = document.querySelector("[data-stock-notify-container]");

    if (!stockElement) return;

    // Réinitialiser les classes
    stockElement.className = "text-sm mt-4 mb-6 sm:mb-8";

    const isOutOfStock = quantity <= 0;

    if (isOutOfStock) {
      stockElement.textContent = "Indisponible";
      stockElement.classList.add("text-pinkyJeannette", "font-medium");

      // Désactiver le bouton
      if (addToCartButton) {
        addToCartButton.disabled = true;
        addToCartButton.classList.remove("cursor-pointer");
        addToCartButton.classList.add("opacity-50", "cursor-not-allowed");
      }
    } else if (quantity <= 3) {
      const plural = quantity > 1 ? "s" : "";
      stockElement.textContent = `Stock faible (${quantity} restant${plural})`;
      stockElement.classList.add("text-pinkyJeannette", "font-medium");

      // Activer le bouton
      if (addToCartButton) {
        addToCartButton.disabled = false;
        addToCartButton.classList.add("cursor-pointer");
        addToCartButton.classList.remove("opacity-50", "cursor-not-allowed");
      }
    } else {
      stockElement.textContent = "En stock";
      stockElement.classList.add("text-wolf-light");

      // Activer le bouton
      if (addToCartButton) {
        addToCartButton.disabled = false;
        addToCartButton.classList.add("cursor-pointer");
        addToCartButton.classList.remove("opacity-50", "cursor-not-allowed");
      }
    }

    // Mettre à jour la visibilité du bouton de notification retour en stock
    if (stockNotifyContainer) {
      stockNotifyContainer.classList.toggle("hidden", !isOutOfStock);

      // Mettre à jour les data attributes du bouton pour la nouvelle variante
      if (isOutOfStock && variantId) {
        const notifyButton = stockNotifyContainer.querySelector("[data-stock-notify-button]");
        if (notifyButton) {
          notifyButton.dataset.variantId = `gid://shopify/ProductVariant/${variantId}`;
          // Reset l'état active car c'est une nouvelle variante
          notifyButton.dataset.active = "false";

          // Récupérer le titre de la variante
          const product = window.productData && window.productData[this.productId];
          if (product) {
            const variant = product.variants.find((v) => v.id === variantId);
            if (variant) {
              notifyButton.dataset.variantTitle = variant.options.join(" / ");
            }
          }

          // Trigger une vérification async de l'état d'inscription
          if (typeof window.checkStockNotifyStatus === "function") {
            window.checkStockNotifyStatus(notifyButton);
          }
        }
      }
    }
  }

  updateURL(variantId) {
    if (!variantId || !window.history.replaceState) return;

    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);
    window.history.replaceState({}, "", url.toString());
  }
}

customElements.define("variant-selects", VariantSelects);
